import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Flame, KeyRound, Cookie, UtensilsCrossed, Wifi, Bath, Baby, Car, Sparkles, Shirt, Lamp, BookOpen,
  MessageCircle, Instagram, ArrowDown, Users, Menu, X, Globe,
} from "lucide-react";
import { useLanguage, waLink, INSTAGRAM_URL } from "@/i18n";
import { photoUrl } from "@/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMENITY_ICONS = [Flame, Lamp, KeyRound, Cookie, UtensilsCrossed, Wifi, Bath, BookOpen, Baby, Car, Sparkles, Shirt];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const SectionHeading = ({ overline, title, testId }) => (
  <Reveal>
    {overline && <p className="text-xs uppercase tracking-[0.25em] text-[#9E4731] mb-4">{overline}</p>}
    <h2 data-testid={testId} className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] max-w-2xl leading-[1.05]">
      {title}
    </h2>
  </Reveal>
);

export default function Landing() {
  const { lang, setLang, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [photos, setPhotos] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState(null);
  const img = (slot) => photoUrl(photos, slot);

  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
    axios.get(`${API}/gallery`).then((r) => setGalleryPhotos(r.data)).catch(() => {});
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    lenisRef.current?.scrollTo(id, { offset: -72 });
  };

  const navLinks = [
    { id: "#espacios", label: t.nav.rooms },
    { id: "#amenidades", label: t.nav.amenities },
    { id: "#reglas", label: t.nav.rules },
    { id: "#contacto", label: t.nav.contact },
  ];

  return (
    <div className="bg-[#F8F5EF] text-[#1A1A1A]" data-testid="landing-page">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <button onClick={() => scrollTo("#inicio")} data-testid="nav-logo" aria-label="ABAITA by Yesod Haolam">
            <img src="/logo.png" alt="ABAITA by Yesod Haolam" className="h-14 w-auto object-contain" />
          </button>
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id.slice(1)}`}
                onClick={() => scrollTo(l.id)}
                className="text-xs uppercase tracking-[0.18em] text-[#4A4D42] hover:text-[#B34A31] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-xl border border-[#1A1A1A]/10 text-xs uppercase tracking-[0.15em] hover:border-[#B34A31]/40 transition-colors"
            >
              <Globe className="w-3.5 h-3.5" /> {lang === "es" ? "EN" : "ES"}
            </button>
            <a
              data-testid="nav-whatsapp-btn"
              href={waLink(t.waMessages.general)}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B34A31] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#9E4731] hover:scale-[1.03] transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4" /> {t.nav.book}
            </a>
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-white/90 backdrop-blur-xl border-t border-[#1A1A1A]/5 px-6 py-4 flex flex-col gap-4" data-testid="mobile-menu">
            {navLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id.slice(1)}`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-sm uppercase tracking-[0.18em] text-[#4A4D42]"
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="inicio" ref={heroRef} className="relative min-h-screen grid lg:grid-cols-2 overflow-hidden" data-testid="hero-section">
        {/* DERECHA: foto ABAITA */}
        <div className="relative order-1 lg:order-2 min-h-[55vh] lg:min-h-screen overflow-hidden">
          <motion.div className="absolute inset-0" style={{ y: heroY, scale: 1.12 }}>
            {img("hero") ? (
              <img src={img("hero")} alt="ABAITA" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[#E5E2DA]" />
            )}
          </motion.div>
        </div>
        {/* IZQUIERDA: fondo gris con texto */}
        <div className="relative order-2 lg:order-1 flex items-center bg-[#E5E2DA]">
          <div className="w-full px-6 md:px-12 xl:px-16 pb-20 pt-16 lg:py-40">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-xs uppercase tracking-[0.3em] text-[#9E4731] mb-6"
              data-testid="hero-overline"
            >
              {t.hero.overline}
            </motion.p>
            <h1 className="font-display text-[#1A1A1A] text-5xl md:text-6xl xl:text-7xl leading-[0.95] tracking-tighter" data-testid="hero-title">
              <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
              <MaskedLine delay={0.5} className="italic text-[#B34A31]">{t.hero.line2}</MaskedLine>
              <MaskedLine delay={0.65}>{t.hero.line3}</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-8 max-w-xl text-[#4A4D42] text-base leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                data-testid="hero-whatsapp-cta"
                href={waLink(t.waMessages.general)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-8 py-4 rounded-full bg-[#B34A31] text-white text-sm uppercase tracking-[0.15em] hover:bg-[#9E4731] hover:scale-[1.03] transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4" /> {t.hero.cta}
              </a>
              <button
                data-testid="hero-availability-cta"
                onClick={() => scrollTo("#espacios")}
                className="flex items-center gap-3 px-8 py-4 rounded-full border border-[#1A1A1A]/30 text-[#1A1A1A] text-sm uppercase tracking-[0.15em] hover:bg-[#1A1A1A]/5 transition-colors duration-300"
              >
                <ArrowDown className="w-4 h-4" /> {t.hero.secondary}
              </button>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-white/80 text-xs uppercase tracking-[0.3em] z-10"
        >
          {t.hero.scroll} <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#36422D] text-[#EAE4D9] py-5 overflow-hidden border-y border-[#1A1A1A]/10" data-testid="amenities-marquee">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center">
              {t.marquee.map((item, i) => (
                <span key={i} className="font-display text-xl md:text-2xl italic px-6 flex items-center gap-6">
                  {item} <span className="text-[#8E9A85] not-italic text-sm">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT / MANIFESTO */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32" data-testid="about-section">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16">
          <div>
            <SectionHeading overline={t.about.overline} title={t.about.title} testId="about-title" />
            <Reveal delay={0.15}>
              <p className="mt-8 text-[#4A4D42] leading-relaxed max-w-md">{t.about.body}</p>
            </Reveal>
          </div>
          <div className="flex flex-col gap-0 border-t border-[#1A1A1A]/10">
            {t.about.chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="grid grid-cols-[64px_1fr] gap-6 py-8 border-b border-[#1A1A1A]/10 group" data-testid={`chapter-${c.n}`}>
                  <span className="font-display text-3xl text-[#B34A31]/70 group-hover:text-[#B34A31] transition-colors">{c.n}</span>
                  <div>
                    <h3 className="font-display text-2xl mb-2">{c.title}</h3>
                    <p className="text-sm text-[#4A4D42] leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ROOMS BENTO */}
      <section id="espacios" className="bg-[#EAE4D9]/60 py-24 md:py-32" data-testid="rooms-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading overline={t.rooms.overline} title={t.rooms.title} testId="rooms-title" />
          <div className="mt-14 grid lg:grid-cols-2 gap-6">
            <Reveal className="lg:row-span-2">
              <RoomCard room={t.rooms.list[0]} image={img("room1")} tall waMsg={t.waMessages.room1} bookLabel={t.rooms.bookRoom} perNight={t.rooms.perNight} guestsLabel={t.rooms.guests} />
            </Reveal>
            <Reveal delay={0.1}>
              <RoomCard room={t.rooms.list[1]} image={img("room2")} waMsg={t.waMessages.room2} bookLabel={t.rooms.bookRoom} perNight={t.rooms.perNight} guestsLabel={t.rooms.guests} />
            </Reveal>
            <Reveal delay={0.2}>
              <RoomCard room={t.rooms.list[2]} image={img("room3")} waMsg={t.waMessages.room3} bookLabel={t.rooms.bookRoom} perNight={t.rooms.perNight} guestsLabel={t.rooms.guests} />
            </Reveal>
          </div>

          {/* WHOLE APARTMENT */}
          <Reveal delay={0.1} className="mt-6">
            <div className="grid md:grid-cols-2 bg-[#36422D] text-[#F8F5EF] rounded-xl overflow-hidden" data-testid="whole-apartment-card">
              <div className="p-10 md:p-14 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.25em] text-[#8E9A85] mb-4">ABAITA · Yesod Haolam</p>
                <h3 className="font-display text-3xl md:text-4xl mb-4">{t.rooms.whole}</h3>
                <p className="text-[#F8F5EF]/70 text-sm leading-relaxed max-w-sm">{t.rooms.wholeText}</p>
                <div className="mt-6 flex items-center gap-6 text-sm">
                  <span className="font-display text-3xl">
                    ${t.rooms.wholePrice} <span className="text-sm text-[#F8F5EF]/60">MXN {t.rooms.perNight}</span>
                  </span>
                  <span className="flex items-center gap-2 text-[#F8F5EF]/60">
                    <Users className="w-4 h-4" /> {t.rooms.wholeCapacity} {t.rooms.guests}
                  </span>
                </div>
                <a
                  data-testid="whole-whatsapp-btn"
                  href={waLink(t.waMessages.whole)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex w-fit items-center gap-3 px-7 py-3.5 rounded-full bg-[#B34A31] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#9E4731] hover:scale-[1.03] transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" /> {t.rooms.bookWhole}
                </a>
              </div>
              <div className="relative min-h-[280px] overflow-hidden">
                {img("hero") ? (
                  <img src={img("hero")} alt={t.rooms.whole} className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="absolute inset-0 bg-[#EAE4D9]" />
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RULES + LOCATION */}
      <section id="reglas" className="max-w-7xl mx-auto px-6 py-24 md:py-32" data-testid="rules-section">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <SectionHeading overline={t.rules.overline} title={t.rules.title} testId="rules-title" />
            <div className="mt-10 border-t border-[#1A1A1A]/10">
              {t.rules.list.map((rule, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex gap-6 py-5 border-b border-[#1A1A1A]/10" data-testid={`rule-${i}`}>
                    <span className="font-display text-lg text-[#B34A31]/70">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-[#4A4D42] leading-relaxed">{rule}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <p className="mt-8 rounded-md border border-[#B34A31]/30 bg-[#B34A31]/5 px-5 py-4 text-sm md:text-base font-semibold text-[#9E4731] uppercase tracking-[0.1em] leading-relaxed" data-testid="rules-notice">
                {t.rules.notice}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div>
              <div className="rounded-xl overflow-hidden border border-[#1A1A1A]/10 shadow-xl shadow-black/5" data-testid="location-map">
                <iframe
                  title="Mapa Interlomas"
                  src="https://www.google.com/maps?q=Torres%20Placet%2C%20Interlomas%2C%20Huixquilucan%2C%20M%C3%A9xico&output=embed"
                  className="w-full aspect-[4/5] md:aspect-[4/4.6]"
                  loading="lazy"
                />
              </div>
              <p className="mt-5 font-display text-2xl md:text-3xl" data-testid="location-caption">{t.contact.location}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AMENITIES */}
      <section id="amenidades" className="bg-[#36422D] text-[#F8F5EF] py-24 md:py-32" data-testid="amenities-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#8E9A85] mb-4">{t.amenities.overline}</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-2xl leading-[1.05]" data-testid="amenities-title">
              {t.amenities.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F8F5EF]/10 border border-[#F8F5EF]/10">
            {t.amenities.list.map((item, i) => {
              const Icon = AMENITY_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="bg-[#36422D] p-8 h-full group hover:bg-[#3d4b33] transition-colors" data-testid={`amenity-${i}`}>
                    <Icon className="w-6 h-6 text-[#8E9A85] mb-5 group-hover:text-[#EAE4D9] transition-colors" />
                    <p className="text-sm text-[#F8F5EF]/85 leading-relaxed">{item}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-[#EAE4D9]/60 py-24 md:py-32 overflow-hidden" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading overline={t.gallery.overline} title={t.gallery.title} testId="gallery-title" />
        </div>
        <div className="mt-14 flex gap-6 px-6 overflow-x-auto pb-4 snap-x" data-testid="gallery-strip">
          {(galleryPhotos?.length
            ? galleryPhotos.map((g) => `${API}/files/${g.storage_path}`)
            : [img("room1"), img("hero"), img("room2"), img("location"), img("room3")].filter(Boolean)
          ).map((src, i) => (
            <div key={i} className="shrink-0 w-[75vw] sm:w-[420px] snap-center overflow-hidden rounded-xl group" data-testid={`gallery-image-${i}`}>
              <img src={src} alt={`ABAITA ${i + 1}`} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="max-w-7xl mx-auto px-6 py-24 md:py-32" data-testid="contact-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading overline={t.contact.overline} title={t.contact.title} testId="contact-title" />
            <Reveal delay={0.1}>
              <p className="mt-8 text-[#4A4D42] leading-relaxed max-w-md">{t.contact.body}</p>
            </Reveal>
          </div>
          <div className="flex flex-col gap-4">
            <Reveal delay={0.1}>
              <a
                data-testid="contact-whatsapp-btn"
                href={waLink(t.waMessages.general)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-4 bg-[#B34A31] text-white rounded-xl p-8 hover:bg-[#9E4731] hover:scale-[1.01] transition-all duration-300 group"
              >
                <div className="flex items-center gap-5">
                  <MessageCircle className="w-8 h-8" />
                  <div>
                    <p className="font-display text-2xl">{t.contact.whatsapp}</p>
                    <p className="text-white/70 text-sm mt-1">+52 55 1706 0230</p>
                  </div>
                </div>
                <span className="text-2xl group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                data-testid="contact-instagram-btn"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-4 bg-white border border-[#1A1A1A]/10 rounded-xl p-8 hover:border-[#B34A31]/40 hover:scale-[1.01] transition-all duration-300 group"
              >
                <div className="flex items-center gap-5">
                  <Instagram className="w-8 h-8 text-[#B34A31]" />
                  <div>
                    <p className="font-display text-2xl">{t.contact.instagram}</p>
                    <p className="text-[#4A4D42] text-sm mt-1">@abaitayesodhaolam</p>
                  </div>
                </div>
                <span className="text-2xl text-[#B34A31] group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#1A1A1A]/10 py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="ABAITA by Yesod Haolam" className="h-12 w-auto object-contain" />
          <p className="text-xs text-[#4A4D42] uppercase tracking-[0.2em]">{t.footer.made}</p>
          <Link to="/admin" className="text-xs text-[#4A4D42]/60 hover:text-[#B34A31] transition-colors uppercase tracking-[0.2em]" data-testid="footer-admin-link">
            {t.footer.admin}
          </Link>
        </div>
      </footer>

      {/* STICKY MOBILE WHATSAPP */}
      <a
        data-testid="sticky-whatsapp-fab"
        href={waLink(t.waMessages.general)}
        target="_blank"
        rel="noopener noreferrer"
        className="sm:hidden fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#B34A31] text-white flex items-center justify-center shadow-xl shadow-black/20"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
}

const RoomCard = ({ room, image, waMsg, bookLabel, perNight, guestsLabel, tall }) => (
  <div className={`bg-white border border-[#1A1A1A]/10 rounded-xl overflow-hidden group flex flex-col ${tall ? "h-full" : ""}`} data-testid={`room-card-${room.id}`}>
    <div className={`overflow-hidden ${tall ? "aspect-[4/3] lg:aspect-auto lg:flex-1 lg:min-h-[320px]" : "aspect-[16/9]"}`}>
      {image ? (
        <img src={image} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      ) : (
        <div className="w-full h-full bg-[#EAE4D9]" />
      )}
    </div>
    <div className="p-8">
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="font-display text-2xl md:text-3xl">{room.name}</h3>
        <p className="font-display text-2xl text-[#B34A31] whitespace-nowrap">
          ${room.price} <span className="text-xs text-[#4A4D42] font-sans">MXN {perNight}</span>
        </p>
      </div>
      <p className="text-sm text-[#4A4D42] leading-relaxed mb-4">{room.desc}</p>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs text-[#4A4D42] uppercase tracking-[0.15em]">
          <Users className="w-4 h-4" /> {room.capacity} {guestsLabel}
        </span>
        <a
          data-testid={`room-whatsapp-btn-${room.id}`}
          href={waLink(waMsg)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#B34A31] hover:text-[#9E4731] transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> {bookLabel}
        </a>
      </div>
    </div>
  </div>
);
